import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ROUTES, STORAGE_KEYS } from '@/constants'
import { useDonorContext } from '@/context/DonorContext'
import { useChairpersonContext } from '@/context/ChairpersonContext'
import localStorageService from '@/services/LocalStorageService'

import {
  BlankLayout,
  DonateLayout,
  DonateLandingRedirect,
  ChairpersonLayout,
  ChairpersonAuthLayout,
} from '@/layouts'
import {
  ProtectedRoute,
  PublicRoute,
  ChairpersonProtectedRoute,
} from '@/components/layout'
import {
  LoginPage,
  DonateNearYou,
  PlaygroundPage,
  ScheduleDonation,
  ResetPasswordPage,
  AccountRecoveryPage,
  DonateNearYouResults,
  LoginWithoutPasswordPage,
  GuestConfirmAppointmentPage,
  DonorAppointments,
  DonorHealthHistory,
  DonorBadges,
  DonorLoyaltyAuth,
  SearchByDonorCenters,
  DonorCenterCalendar,
  SearchByZipCode,
  SearchByZipCodeResults,
  SearchByCity,
  SearchByCityResults,
  SearchBySponsor,
  SearchBySponsorResults,
  DonateNavNotConfiguredPage,
  ChairpersonLoginPage,
  ChairpersonDashboardPage,
  ChairpersonScheduleDrivePage,
  ChairpersonDriveResultsPage,
  ChairpersonDriveDonorsPage,
  ChairpersonDriveDonorsComposeEmailPage,
  ChairpersonDriveDonorsReviewSendPage,
  ChairpersonAccountRecoveryPage,
  ChairpersonResetPasswordPage,
} from '@/pages'

const AppRoutes = () => {
  const location = useLocation()
  const { donor, loadingDonor } = useDonorContext()
  const { chairperson, loadingChairperson } = useChairpersonContext()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlRedirect = searchParams.get('redirect_url')
    const storedRedirect = localStorageService.getItem(
      STORAGE_KEYS.REDIRECT_URL
    )
    const isAuthRoute = location.pathname.startsWith(ROUTES.AUTH)
    const isChairpersonResetRoute =
      location.pathname === ROUTES.CHAIRPERSON_RESET_PASSWORD
    const isRedirectPreservingRoute = isAuthRoute || isChairpersonResetRoute

    if (isRedirectPreservingRoute) {
      if (urlRedirect && urlRedirect !== storedRedirect) {
        localStorageService.setItem(STORAGE_KEYS.REDIRECT_URL, urlRedirect)
      }
      if (!urlRedirect && storedRedirect) {
        const url = new URL(window.location.href)
        url.searchParams.set('redirect_url', storedRedirect)
        window.history.replaceState(
          null,
          '',
          url.pathname + url.search + url.hash
        )
      }
    } else {
      if (urlRedirect) {
        const url = new URL(window.location.href)
        url.searchParams.delete('redirect_url')
        window.history.replaceState(
          null,
          '',
          url.pathname + url.search + url.hash
        )
      }
      if (storedRedirect) {
        localStorageService.removeItem(STORAGE_KEYS.REDIRECT_URL)
      }
    }
  }, [location.pathname, location.search])

  if (
    !loadingDonor &&
    donor?.dp_donor_auth?.require_password_reset &&
    location.pathname !== ROUTES.RESET_PASSWORD
  )
    return (
      <Navigate
        to={`${ROUTES.RESET_PASSWORD}?redirect_url=${encodeURIComponent(
          typeof window !== 'undefined'
            ? window.location.href
            : location.pathname + location.search
        )}`}
        replace
      />
    )

  const isChairpersonProtectedPath =
    location.pathname.startsWith('/chairperson') &&
    location.pathname !== ROUTES.CHAIRPERSON_LOGIN &&
    location.pathname !== ROUTES.CHAIRPERSON_ACCOUNT_RECOVERY &&
    location.pathname !== ROUTES.CHAIRPERSON_RESET_PASSWORD

  if (
    isChairpersonProtectedPath &&
    !loadingChairperson &&
    chairperson?.dp_chairperson_auth?.require_password_reset
  )
    return (
      <Navigate
        to={`${ROUTES.CHAIRPERSON_RESET_PASSWORD}?redirect_url=${encodeURIComponent(
          typeof window !== 'undefined'
            ? window.location.href
            : location.pathname + location.search
        )}`}
        replace
      />
    )

  return (
    <Routes>
      <Route path='/' element={<Navigate to='/donate' replace />} />

      {/* Blank layout routes - public playground */}
      <Route element={<BlankLayout />}>
        <Route path='/playground' element={<PlaygroundPage />} />
      </Route>

      {/* Protected blank layout routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<BlankLayout />}>
          <Route
            path={ROUTES.DONOR_LOYALTY_AUTH}
            element={<DonorLoyaltyAuth />}
          />
        </Route>
      </Route>

      {/* Public routes - accessible only when NOT authenticated */}
      <Route element={<PublicRoute />}>
        <Route element={<DonateLayout />}>
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          {/* Not needed for now, login and account recovery are the only auth routes */}
          {/* <Route path={ROUTES.REGISTER} element={<RegisterPage />} /> */}
          <Route
            path={ROUTES.LOGIN_WITHOUT_PASSWORD}
            element={<LoginWithoutPasswordPage />}
          />
          <Route
            path={ROUTES.ACCOUNT_RECOVERY}
            element={<AccountRecoveryPage />}
          />
        </Route>
      </Route>

      {/* Protected routes - require authentication */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DonateLayout />}>
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
          <Route
            path={ROUTES.DONOR_APPOINTMENTS}
            element={<DonorAppointments />}
          />
          <Route
            path={ROUTES.DONOR_HEALTH_HISTORY}
            element={<DonorHealthHistory />}
          />
          <Route path={ROUTES.DONOR_BADGES} element={<DonorBadges />} />
        </Route>
      </Route>

      {/* Public donation layout routes */}
      <Route element={<DonateLayout />}>
        <Route path={ROUTES.DONATE} element={<DonateLandingRedirect />} />
        <Route
          path={ROUTES.DONATE_NAV_NOT_CONFIGURED}
          element={<DonateNavNotConfiguredPage />}
        />
        <Route path={ROUTES.DONATE_NEAR_YOU} element={<DonateNearYou />} />
        <Route
          path={ROUTES.DONATE_NEAR_YOU_RESULTS}
          element={<DonateNearYouResults />}
        />
        <Route path={ROUTES.DONATE_SPONSOR} element={<SearchBySponsor />} />
        <Route
          path={ROUTES.DONATE_SPONSOR_RESULTS}
          element={<SearchBySponsorResults />}
        />
        <Route
          path={ROUTES.DONATE_PRIVATE_DRIVES}
          element={
            <Navigate
              to={`${ROUTES.DONATE_SPONSOR}?mode=private_event`}
              replace
            />
          }
        />
        <Route
          path={ROUTES.DONATE_EVENT}
          element={<div>Placeholder content for Search by Event.</div>}
        />
        <Route
          path={ROUTES.DONATE_DONOR_CENTERS}
          element={<SearchByDonorCenters />}
        />
        <Route
          path={ROUTES.DONATE_DONOR_CENTERS_CALENDAR}
          element={<DonorCenterCalendar />}
        />
        <Route path={ROUTES.DONATE_ZIP_CODE} element={<SearchByZipCode />} />
        <Route
          path={ROUTES.DONATE_ZIP_CODE_RESULTS}
          element={<SearchByZipCodeResults />}
        />
        <Route path={ROUTES.DONATE_CITY} element={<SearchByCity />} />
        <Route
          path={ROUTES.DONATE_CITY_RESULTS}
          element={<SearchByCityResults />}
        />
        <Route path={ROUTES.SCHEDULE_DONATION} element={<ScheduleDonation />} />
        <Route
          path={ROUTES.GUEST_CONFIRM_APPOINTMENT}
          element={<GuestConfirmAppointmentPage />}
        />
      </Route>

      {/* Chairperson routes - public login and account recovery */}
      <Route element={<ChairpersonAuthLayout />}>
        <Route
          path={ROUTES.CHAIRPERSON_LOGIN}
          element={<ChairpersonLoginPage />}
        />
        <Route
          path={ROUTES.CHAIRPERSON_ACCOUNT_RECOVERY}
          element={<ChairpersonAccountRecoveryPage />}
        />
      </Route>

      {/* Chairperson routes - protected */}
      <Route element={<ChairpersonProtectedRoute />}>
        <Route element={<ChairpersonAuthLayout />}>
          <Route
            path={ROUTES.CHAIRPERSON_RESET_PASSWORD}
            element={<ChairpersonResetPasswordPage />}
          />
        </Route>
        <Route element={<ChairpersonLayout />}>
          <Route
            path={ROUTES.CHAIRPERSON_DASHBOARD}
            element={<ChairpersonDashboardPage />}
          />
          <Route
            path={ROUTES.CHAIRPERSON_SCHEDULE_DRIVE}
            element={<ChairpersonScheduleDrivePage />}
          />
          <Route
            path={ROUTES.CHAIRPERSON_DRIVE_RESULTS}
            element={<ChairpersonDriveResultsPage />}
          />
          <Route
            path={ROUTES.CHAIRPERSON_DRIVE_DONORS}
            element={<ChairpersonDriveDonorsPage />}
          />
          <Route
            path={ROUTES.CHAIRPERSON_DRIVE_DONORS_EMAIL_COMPOSE}
            element={<ChairpersonDriveDonorsComposeEmailPage />}
          />
          <Route
            path={ROUTES.CHAIRPERSON_DRIVE_DONORS_EMAIL_REVIEW}
            element={<ChairpersonDriveDonorsReviewSendPage />}
          />
          <Route
            path={ROUTES.CHAIRPERSON}
            element={<Navigate to={ROUTES.CHAIRPERSON_DASHBOARD} replace />}
          />
        </Route>
      </Route>
    </Routes>
  )
}

export default AppRoutes
